// apps/web/src/pages/cadastros/tipo-material-combobox.tsx — Fase 03, Plano 08 (CAD-04).
// Lista em portal (Popover) para não ficar presa no overflow do modal.
import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';
import type { TipoMaterialCategoria } from '@pieltec/contracts';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

interface TipoMaterialComboboxProps {
  value?: string;
  onChange: (categoriaId: string) => void;
  disabled?: boolean;
}

function ehCategoriaTipoMaterial(valor: unknown): valor is TipoMaterialCategoria {
  if (typeof valor !== 'object' || valor === null) return false;
  const categoria = valor as Record<string, unknown>;
  return (
    typeof categoria.id === 'string' &&
    typeof categoria.codigo === 'string' &&
    typeof categoria.nome === 'string'
  );
}

export const TipoMaterialCombobox: React.FC<TipoMaterialComboboxProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [categorias, setCategorias] = useState<TipoMaterialCategoria[]>([]);
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(false);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const buscaRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let active = true;
    const carregar = async () => {
      setLoading(true);
      setErroCarga(null);
      try {
        const res = await fetch('/api/v1/cadastros/tipo-material?ativoApenas=true');
        if (!active) return;
        if (res.ok) {
          const json: unknown = await res.json();
          setCategorias(Array.isArray(json) ? json.filter(ehCategoriaTipoMaterial) : []);
        } else {
          setErroCarga('Não foi possível carregar as categorias.');
        }
      } catch {
        if (active) setErroCarga('Não foi possível carregar as categorias.');
      } finally {
        if (active) setLoading(false);
      }
    };
    void carregar();
    return () => {
      active = false;
    };
  }, []);

  const filtradas = categorias.filter((c) => {
    const termo = busca.toLowerCase();
    return c.nome.toLowerCase().includes(termo) || c.codigo.toLowerCase().includes(termo);
  });

  const selecionada = categorias.find((c) => c.id === value || c.codigo === value);

  return (
    <Popover
      open={open}
      onOpenChange={(proximo) => {
        setOpen(proximo);
        if (!proximo) setBusca('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          className="w-full flex items-center justify-between min-h-[44px] px-3 py-2 bg-white border border-slate-300 rounded-md text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 disabled:opacity-50"
        >
          <span className="truncate">
            {selecionada ? selecionada.nome : 'Selecione a categoria técnica...'}
          </span>
          <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0 overflow-hidden"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          buscaRef.current?.focus();
        }}
      >
        <div className="p-2 border-b bg-slate-50 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden="true" />
          <input
            ref={buscaRef}
            id="tipo-material-busca"
            type="text"
            aria-label="Buscar categoria técnica"
            placeholder="Buscar categoria técnica..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="w-full bg-transparent text-xs text-slate-900 focus:outline-none"
          />
        </div>

        <div className="max-h-60 overflow-y-auto p-1" role="listbox">
          {loading ? (
            <div className="p-4 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
              Carregando categorias...
            </div>
          ) : erroCarga ? (
            <div className="p-4 text-center text-xs text-rose-700">{erroCarga}</div>
          ) : filtradas.length === 0 ? (
            <div className="p-4 text-center text-xs text-slate-400">
              Nenhuma categoria técnica encontrada.
            </div>
          ) : (
            filtradas.map((cat) => {
              const isSelected = cat.id === value || cat.codigo === value;
              return (
                <button
                  key={cat.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(cat.id);
                    setOpen(false);
                    setBusca('');
                  }}
                  className={`w-full min-h-[44px] flex items-center justify-between px-3 py-2 text-left text-sm rounded-md transition-colors ${
                    isSelected
                      ? 'bg-slate-900 text-white font-semibold'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex flex-col truncate pr-2">
                    <span className="truncate">{cat.nome}</span>
                    <span
                      className={`text-[10px] font-mono ${
                        isSelected ? 'text-slate-300' : 'text-slate-400'
                      }`}
                    >
                      {cat.codigo}
                    </span>
                  </div>
                  {isSelected && <Check className="w-4 h-4 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
